import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import { getBalanceByUserId, updateBalance } from '../../Common/SharedApis/Auth';

interface BalancePopupProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  action: 'deposit' | 'withdraw';
  onBalanceUpdate: (userId: string, newBalance: number) => void; // Added
}

const BalancePopup: React.FC<BalancePopupProps> = ({ open, onClose, userId, action ,onBalanceUpdate}) => {
  const [amount, setAmount] = useState('');
  const [currentBalance, setCurrentBalance] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch balance when the modal opens
  useEffect(() => {
    if (open) {
      fetchBalance();
    }
  }, [open, userId]);

  const fetchBalance = async () => {
    setLoading(true);
    setCurrentBalance(0);
    try {
      const balance = await getBalanceByUserId(userId);
      setCurrentBalance(balance.avaliableBalance); // Note: Matches API spelling
    } catch (err) {
      setError('Failed to fetch balance');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    // For withdrawals, check if sufficient balance exists
    if (action === 'withdraw' && currentBalance !== null && amountNum > currentBalance) {
      setError('Insufficient balance');
      return;
    }

    setLoading(true);
    try {
      const isWithdrawal = action === 'withdraw';
      const lockingBalance = isWithdrawal ? amountNum : 0; // Locking balance is 0 for deposits
      await updateBalance(userId, amountNum, lockingBalance, isWithdrawal);
      alert(`${action.charAt(0).toUpperCase() + action.slice(1)} successful`);

      // Calculate the new balance
      const newBalance = isWithdrawal
        ? currentBalance - amountNum
        : currentBalance + amountNum;

        setAmount(''); // Reset form
      // Update the grid using the callback
      onBalanceUpdate(userId, newBalance);

     
      setCurrentBalance(0);
   
      onClose(); // Close the popup

    } catch (err) {
      setError(`Failed to ${action}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          p: 4,
          width: 400,
          borderRadius: 2,
        }}
      >
        <Typography variant="h6">
          {action === 'deposit' ? 'Deposit' : 'Withdraw'} Balance
        </Typography>
        {loading ? (
          <CircularProgress sx={{ display: 'block', mx: 'auto', mt: 2 }} />
        ) : (
          <>
            {currentBalance !== null && (
              <Typography sx={{ mt: 1 }}>
                Current Available Balance: ${currentBalance.toFixed(2)}
              </Typography>
            )}
            <TextField
              label="Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              margin="normal"
              type="number"
              inputProps={{ min: 0, step: '0.01' }}
            />
            {error && (
              <Typography color="error" sx={{ mt: 1 }}>
                {error}
              </Typography>
            )}
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
              >
                {action === 'deposit' ? 'Deposit' : 'Withdraw'}
              </Button>
              <Button onClick={onClose} variant="outlined">
                Cancel
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default BalancePopup;